import { ChevronRight, X } from '@untitledui/icons';

import {
  getTimePeriod,
  type TimePeriod,
} from '@/pages/dashboard/hooks/useDashboardData';
import { cx } from '@/utils/cx';

function buildDailyCopy(
  timePeriod: TimePeriod,
  meetingsNotesCount: number,
  todayActionCount: number
): { title: string; sub: string } {
  const title =
    timePeriod === 'afternoon'
      ? "Wrapping up? Review today's actions"
      : 'Day in review';
  const parts: string[] = [];
  if (meetingsNotesCount > 0) {
    parts.push(
      `${meetingsNotesCount} meeting${meetingsNotesCount === 1 ? '' : 's'} need${meetingsNotesCount === 1 ? 's' : ''} a summary`
    );
  }
  if (todayActionCount > 0) {
    parts.push(
      `${todayActionCount} new action${todayActionCount === 1 ? '' : 's'} today`
    );
  }
  return { title, sub: parts.join(' · ') };
}

function buildWeeklyCopy(
  weeklyCreated: number,
  weeklyApproved: number
): { title: string; sub: string } {
  const parts: string[] = [];
  if (weeklyCreated > 0) {
    parts.push(
      `${weeklyCreated} action${weeklyCreated === 1 ? '' : 's'} this week`
    );
  }
  if (weeklyApproved > 0) {
    parts.push(`${weeklyApproved} approved`);
  }
  return { title: 'Week in review', sub: parts.join(' · ') };
}

export interface RecapCTAProps {
  showWeeklyCTA: boolean;
  showDailyCTA?: boolean;
  meetingsNeedingNotesCount: number;
  todayActionCount: number;
  weeklyActionsCreated: number;
  weeklyActionsApproved: number;
  onActivateDaily: () => void;
  onActivateWeekly: () => void;
  onDismissDaily: () => void;
  onDismissWeekly: () => void;
}

interface CTABannerProps {
  variant: 'daily' | 'weekly';
  title: string;
  sub: string;
  onClick: () => void;
  onDismiss: () => void;
}

function CTABanner({
  variant,
  title,
  sub,
  onClick,
  onDismiss,
}: CTABannerProps) {
  return (
    <div
      data-component='recap-cta'
      className={cx(
        'w-full flex items-center rounded-lg',
        'bg-brand-solid shadow-xs-skeumorphic',
        'hover:bg-brand-solid_hover transition-colors'
      )}
    >
      <button
        type='button'
        onClick={onClick}
        data-instance={`${variant}-recap`}
        className='min-w-0 flex-1 flex items-center gap-3 px-4 py-3 text-left cursor-pointer rounded-l-lg'
      >
        <div className='min-w-0 flex-1'>
          <p className='text-[13px] font-semibold text-white'>{title}</p>
          {sub && <p className='text-[12px] text-white/70'>{sub}</p>}
        </div>
        <ChevronRight className='size-5 text-white/80 shrink-0' />
      </button>
      <button
        type='button'
        aria-label='Dismiss'
        onClick={onDismiss}
        className='p-1 mr-3 rounded hover:bg-white/15 transition-colors'
      >
        <X className='size-4 text-white/70' />
      </button>
    </div>
  );
}

export function RecapCTA({
  showWeeklyCTA,
  showDailyCTA = true,
  meetingsNeedingNotesCount,
  todayActionCount,
  weeklyActionsCreated,
  weeklyActionsApproved,
  onActivateDaily,
  onActivateWeekly,
  onDismissDaily,
  onDismissWeekly,
}: RecapCTAProps) {
  const timePeriod = getTimePeriod();
  const dailyCopy = buildDailyCopy(
    timePeriod,
    meetingsNeedingNotesCount,
    todayActionCount
  );
  const weeklyCopy = buildWeeklyCopy(
    weeklyActionsCreated,
    weeklyActionsApproved
  );
  return (
    <div className='space-y-2'>
      {showWeeklyCTA && (
        <CTABanner
          variant='weekly'
          title={weeklyCopy.title}
          sub={weeklyCopy.sub}
          onClick={onActivateWeekly}
          onDismiss={onDismissWeekly}
        />
      )}
      {showDailyCTA && (
        <CTABanner
          variant='daily'
          title={dailyCopy.title}
          sub={dailyCopy.sub}
          onClick={onActivateDaily}
          onDismiss={onDismissDaily}
        />
      )}
    </div>
  );
}
